import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Info } from "lucide-react";
import { CTASection } from "@/components/common/CTASection";
import { Reveal } from "@/components/common/Reveal";
import { CASE_STUDIES } from "@/lib/content";
import { lineReveal } from "@/lib/motion";

export default function StoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const story = CASE_STUDIES.find((s) => s.slug === slug);
  const others = CASE_STUDIES.filter((s) => s.slug !== slug);

  if (!story) {
    return (
      <div className="container-px flex min-h-[70vh] flex-col items-center justify-center pt-[72px] text-center">
        <h1 className="font-head text-3xl font-bold text-white">Story not found</h1>
        <Link to="/stories" className="btn-primary mt-6" data-testid="back-to-stories">Back to Stories</Link>
      </div>
    );
  }

  return (
    <>
      <article className="relative pt-[72px]" data-testid="story-page">
        {/* header */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img src={story.image} alt="" className="h-full w-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0E0E10]/70 to-[#0E0E10]" />
          </div>
          <div className="container-px relative py-16 lg:py-20">
            <Link to="/stories" data-testid="story-back-link" className="mb-8 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors hover:text-orange-400">
              <ArrowLeft className="h-4 w-4" /> Back to Stories
            </Link>
            <div className="max-w-3xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-orange-500/20 bg-orange-500/5 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-orange-300">{story.sector}</span>
                <span className="rounded-full border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-zinc-400">{story.tag}</span>
              </div>
              <div className="reveal-mask mt-5 pb-2">
                <motion.h1 variants={lineReveal} initial="hidden" animate="show" className="font-head text-3xl font-extrabold leading-[1.12] tracking-tight text-white sm:text-4xl lg:text-5xl">
                  {story.title}
                </motion.h1>
              </div>
              <div className="mt-6 inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-zinc-400" data-testid="story-illustrative-badge">
                <Info className="h-4 w-4 text-orange-400" /> Illustrative scenario — how we'd approach it. Not a claim of a specific past client.
              </div>
            </div>
          </div>
        </div>

        <div className="container-px pb-4">
          <div className="mx-auto max-w-3xl">
            <div className="overflow-hidden rounded-[2rem] border border-white/10">
              <img src={story.image} alt={story.title} className="h-64 w-full object-cover sm:h-80" />
            </div>

            {/* The situation */}
            <div className="mt-12">
              <h2 className="kicker mb-4 flex items-center gap-2"><span className="h-px w-8 bg-orange-400/60" /> The Situation</h2>
              <div className="space-y-5">
                {story.situation.map((p, i) => (
                  <p key={i} className="text-base leading-[1.8] text-zinc-300">{p}</p>
                ))}
              </div>
              <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-orange-500/20 bg-orange-500/5 px-4 py-2 text-sm text-orange-300">
                Suggested model: <span className="font-semibold text-white">{story.model}</span>
              </div>
            </div>
          </div>
        </div>

        {/* The HATCH approach */}
        <div className="border-y border-white/10 bg-[#131316] py-16">
          <div className="container-px">
            <div className="mx-auto max-w-3xl">
              <h2 className="font-head text-2xl font-bold text-white sm:text-3xl">The HATCH approach</h2>
              <p className="mt-3 text-zinc-400">How we'd move this from vision to a working India operation, stage by stage.</p>
            </div>
            <div className="mx-auto mt-10 max-w-3xl">
              <div className="relative">
                <div className="absolute left-6 top-2 h-[calc(100%-1rem)] w-px bg-gradient-to-b from-orange-500/50 via-amber-500/30 to-transparent" />
                {story.approach.map((a, i) => (
                  <Reveal key={i} i={i}>
                    <div className="relative mb-6 flex gap-6">
                      <div className="z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl grad-accent font-head text-lg font-extrabold text-[#1a0f00]">{a.letter}</div>
                      <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
                        <h3 className="font-head text-lg font-bold text-white">{a.title}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-zinc-400">{a.text}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* What success looks like */}
        <div className="container-px py-16">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-head text-2xl font-bold text-white sm:text-3xl">What success looks like</h2>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {story.results.map((r, i) => (
                <Reveal key={i} i={i}>
                  <div className="flex items-start gap-3 rounded-2xl border border-white/8 bg-white/[0.02] p-5">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
                    <span className="text-sm text-zinc-200">{r}</span>
                  </div>
                </Reveal>
              ))}
            </div>

            <div className="mt-12 rounded-2xl border border-orange-500/20 bg-[#17171A] p-8 text-center">
              <p className="font-head text-xl font-bold text-white">Is this your situation?</p>
              <p className="mx-auto mt-3 max-w-md text-sm text-zinc-400">Let's talk through how the HATCH Framework™ would apply to your specific goals.</p>
              <button onClick={() => navigate("/contact")} className="btn-primary mx-auto mt-6" data-testid="story-cta">
                Book a Free Consultation <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* other stories */}
        <div className="border-t border-white/10 bg-[#131316] py-16">
          <div className="container-px">
            <h3 className="mb-8 font-head text-2xl font-bold text-white">More scenarios</h3>
            <div className="grid gap-6 md:grid-cols-2">
              {others.map((s) => (
                <Link key={s.slug} to={`/stories/${s.slug}`} data-testid={`story-related-${s.slug}`} className="group flex gap-5 overflow-hidden rounded-2xl border border-white/8 bg-white/[0.02] p-4 transition-colors hover:border-orange-500/30">
                  <img src={s.image} alt={s.title} className="h-24 w-28 shrink-0 rounded-xl object-cover" />
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-orange-400">{s.sector}</span>
                    <h4 className="mt-2 font-head text-base font-bold text-white group-hover:text-orange-400">{s.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>

      <CTASection />
    </>
  );
}
